import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

interface ZonePlan {
  name: string;
  lines: string[];
  floorRange: { top: number; bottom: number };
  visitEvery: number;
  overrides: string[];
  exceptions: string[];
}

const OfficetelZonePage: React.FC = () => {
  const { data: complexesData, isLoading: complexesLoading, refetch: refetchComplexes } = useQuery({
    queryKey: ['complexes'],
    queryFn: async () => {
      const response = await fetch('/api/complexes');
      if (!response.ok) throw new Error('단지 목록을 불러오지 못했습니다');
      return response.json();
    },
  });

  const [selectedComplexId, setSelectedComplexId] = useState<number | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);
  const [uploadingOfficetel, setUploadingOfficetel] = useState(false);
  const [forceSelectAllBuildings, setForceSelectAllBuildings] = useState(false);
  const [newDongNumber, setNewDongNumber] = useState(801);
  const [newMaxFloor, setNewMaxFloor] = useState(8);
  const [newUnitPatternInput, setNewUnitPatternInput] = useState('1,2');
  const [newComplexId, setNewComplexId] = useState<number | null>(null);
  const [newComplexName, setNewComplexName] = useState('오피스텔 단지');
  const [newComplexAddress, setNewComplexAddress] = useState('');
  const [showAllComplexes, setShowAllComplexes] = useState(false);

  const {
    data: unitsData,
    isLoading: unitsLoading,
    refetch: refetchUnits,
  } = useQuery({
    queryKey: ['units', selectedBuildingId],
    queryFn: async () => {
      if (!selectedBuildingId) return null;
      const response = await fetch(`/api/units?buildingId=${selectedBuildingId}`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || '세대 정보를 불러오지 못했습니다');
      }
      return response.json();
    },
    enabled: Boolean(selectedBuildingId),
  });

  const [linesInput, setLinesInput] = useState('801,802,803,804,805,806,807,808,809,810');
  const [floorTop, setFloorTop] = useState(8);
  const [floorBottom, setFloorBottom] = useState(3);
  const [floorsPerZone, setFloorsPerZone] = useState(3);
  const [linesPerZone, setLinesPerZone] = useState(2);

  const [defaultVisitEvery, setDefaultVisitEvery] = useState(2);
  const [overrideVisitEvery, setOverrideVisitEvery] = useState(1);
  const [overrideLinesInput, setOverrideLinesInput] = useState('804,805');
  const [exceptionsInput, setExceptionsInput] = useState('-1,-2,한자리,끝호수');

  const parsedLines = useMemo(
    () =>
      linesInput
        .split(',')
        .map(item => item.trim())
        .filter(Boolean),
    [linesInput]
  );

  const parsedOverrideLines = useMemo(
    () =>
      overrideLinesInput
        .split(',')
        .map(item => item.trim())
        .filter(Boolean),
    [overrideLinesInput]
  );

  const parsedExceptions = useMemo(
    () =>
      exceptionsInput
        .split(',')
        .map(item => item.trim())
        .filter(Boolean),
    [exceptionsInput]
  );

  const floorRanges = useMemo(() => {
    const ranges: Array<{ top: number; bottom: number }> = [];
    let currentTop = floorTop;
    while (currentTop >= floorBottom) {
      const bottom = Math.max(floorBottom, currentTop - floorsPerZone + 1);
      ranges.push({ top: currentTop, bottom });
      currentTop = bottom - 1;
    }
    return ranges;
  }, [floorTop, floorBottom, floorsPerZone]);

  const lineChunks = useMemo(() => {
    const chunks: string[][] = [];
    for (let i = 0; i < parsedLines.length; i += linesPerZone) {
      chunks.push(parsedLines.slice(i, i + linesPerZone));
    }
    return chunks;
  }, [parsedLines, linesPerZone]);

  const zonePlans: ZonePlan[] = useMemo(() => {
    const plans: ZonePlan[] = [];
    let zoneIndex = 0;
    floorRanges.forEach(range => {
      lineChunks.forEach(lines => {
        const name = String.fromCharCode(65 + zoneIndex);
        plans.push({
          name: `구역 ${name}`,
          lines,
          floorRange: range,
          visitEvery: defaultVisitEvery,
          overrides: parsedOverrideLines,
          exceptions: parsedExceptions.length > 0 ? parsedExceptions : ['-1', '-2', '한자리', '끝호수'],
        });
        zoneIndex += 1;
      });
    });
    return plans;
  }, [floorRanges, lineChunks, defaultVisitEvery, parsedOverrideLines, parsedExceptions]);

  // Matrix data
  const matrix = useMemo(() => {
    if (!unitsData?.data || unitsData.data.length === 0) return null;
    const units = unitsData.data as Array<{ id: number; floor: number; suffix: number | null; code: string }>;
    const floors = Array.from(new Set(units.map(u => u.floor))).sort((a, b) => b - a);
    const lines = Array.from(new Set(units.map(u => u.code || String(u.suffix ?? '')))).sort((a, b) =>
      a.localeCompare(b, 'ko', { numeric: true })
    );

    const grid = floors.map(floor => {
      return lines.map(line => {
        const match = units.find(u => u.floor === floor && (u.code === line || String(u.suffix ?? '') === line));
        return match;
      });
    });

    return { floors, lines, grid };
  }, [unitsData?.data]);

  // Sync selection defaults when complexes are loaded
  const officetelComplexes = useMemo(
    () =>
      (complexesData?.data || []).filter(
        (c: any) =>
          c.type === 'officetel' || c.groupName === 'officetel' || c.groupName === 'officetel-standalone'
      ),
    [complexesData?.data]
  );

  useEffect(() => {
    if (selectedComplexId !== null) return;
    const list = showAllComplexes ? complexesData?.data : officetelComplexes;
    if (list && list.length > 0) {
      setSelectedComplexId(list[0].id);
    }
  }, [complexesData?.data, officetelComplexes, selectedComplexId, showAllComplexes]);

  useEffect(() => {
    if (!complexesData?.data || selectedComplexId === null) return;
    const complex = complexesData.data.find((c: any) => c.id === selectedComplexId);
    if (complex && complex.buildings && complex.buildings.length > 0) {
      const officetel = complex.buildings.find((b: any) => b.buildingType === 'officetel');
      setSelectedBuildingId(officetel ? officetel.id : complex.buildings[0].id);
    }
  }, [complexesData?.data, selectedComplexId]);

  const setBuildingTypeOfficetel = useMutation({
    mutationFn: async (buildingId: number) => {
      const res = await fetch(`/api/buildings/${buildingId}/classification`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildingType: 'officetel', hasElevator: true }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || '오피스텔로 설정 실패');
      }
      return body;
    },
    onSuccess: () => {
      toast.success('건물을 오피스텔로 설정했습니다.');
      if (selectedBuildingId) {
        refetchUnits();
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const createOfficetelComplex = useMutation({
    mutationFn: async () => {
      const payload = {
        name: newComplexName,
        address: newComplexAddress || 'N/A',
        alias: newComplexName,
      };
      const res = await fetch('/api/officetel/complexes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || '오피스텔 단지 생성 실패');
      }
      return body;
    },
    onSuccess: body => {
      toast.success('오피스텔 단지를 생성했습니다.');
      refetchComplexes();
      if (body?.data?.id) {
        setSelectedComplexId(body.data.id);
        setNewComplexId(body.data.id);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const createOfficetelBuilding = useMutation({
    mutationFn: async () => {
      const unitPattern = newUnitPatternInput
        .split(',')
        .map(s => Number.parseInt(s.trim(), 10))
        .filter(n => !Number.isNaN(n) && n > 0);
      if (unitPattern.length === 0) {
        throw new Error('호수 패턴을 입력하세요');
      }
      const payload: any = {
        dongNumber: newDongNumber,
        maxFloor: newMaxFloor,
        unitPattern,
        hasElevator: true,
        buildingType: 'officetel',
      };
      if (newComplexId) {
        payload.complexId = newComplexId;
      }
      const res = await fetch('/api/officetel/buildings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || '오피스텔 건물 생성 실패');
      }
      return body;
    },
    onSuccess: () => {
      toast.success('오피스텔 건물을 생성했습니다.');
      refetchComplexes();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteOfficetelBuilding = useMutation({
    mutationFn: async (buildingId: number) => {
      const res = await fetch(`/api/officetel/buildings/${buildingId}`, {
        method: 'DELETE',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || '삭제 실패');
      }
      return body;
    },
    onSuccess: () => {
      toast.success('건물을 삭제했습니다.');
      refetchComplexes();
      setSelectedBuildingId(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const generateCardsMutation = useMutation({
    mutationFn: async () => {
      if (!selectedBuildingId) throw new Error('건물을 선택해주세요');
      const payload = {
        buildingIds: [selectedBuildingId],
        rules: {
          unitsPerCard: 50,
          shuffleMethod: 'none',
          buildingTypeRules: {
            officetel: {
              visitEvery: defaultVisitEvery,
              exceptions: parsedExceptions,
              appliesTo: 'all',
              overrides: parsedOverrideLines.reduce<Record<string, { visitEvery: number }>>(
                (acc, line) => {
                  acc[line] = { visitEvery: overrideVisitEvery };
                  return acc;
                },
                {}
              ),
            },
          },
        },
      };

      const response = await fetch('/api/cards/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || '카드 생성에 실패했습니다');
      }
      return response.json();
    },
    onSuccess: data => {
      toast.success(`${data.data?.length ?? 0}개의 카드를 생성했습니다`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleCopy = async () => {
    try {
      const payload = {
        type: 'officetel',
        gapRule: {
          visitEvery: defaultVisitEvery,
          exceptions: parsedExceptions,
          overrides: parsedOverrideLines.reduce<Record<string, { visitEvery: number }>>((acc, line) => {
            acc[line] = { visitEvery: overrideVisitEvery };
            return acc;
          }, {}),
        },
        zones: zonePlans.map(zone => ({
          name: zone.name,
          lines: zone.lines,
          floorRange: zone.floorRange,
          visitEvery: zone.visitEvery,
          overrides: zone.overrides,
          exceptions: zone.exceptions,
        })),
      };
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      toast.success('구역 계획 JSON을 클립보드에 복사했습니다.');
    } catch (error) {
      console.error(error);
      toast.error('복사에 실패했습니다.');
    }
  };

  return (
    <div className="page-wide">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">오피스텔 모드</h1>
          <p className="text-gray-600 text-sm mt-1">
            라인×층 기반 소구역과 간격 규칙(gapRule)을 포함한 구역 계획을 설계하세요.
          </p>
        </div>
        <button
          onClick={handleCopy}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm"
        >
          구역 계획 JSON 복사
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 border rounded-lg p-4 space-y-3">
          <h2 className="text-lg font-semibold text-gray-800">1단계) 오피스텔 단지 생성</h2>
          <p className="text-sm text-gray-600">
            오피스텔 단지를 먼저 생성하고, 이어서 건물을 생성하세요. 단지 미선택 시 단독 단지가 자동 생성됩니다.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">단지명</label>
              <input
                type="text"
                value={newComplexName}
                onChange={e => setNewComplexName(e.target.value)}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-700 mb-1">주소</label>
              <input
                type="text"
                value={newComplexAddress}
                onChange={e => setNewComplexAddress(e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="선택"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => createOfficetelComplex.mutate()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded disabled:opacity-50"
              disabled={createOfficetelComplex.isPending}
            >
              {createOfficetelComplex.isPending ? '생성 중...' : '오피스텔 단지 생성'}
            </button>
            <p className="text-xs text-gray-500 self-center">
              생성 후 바로 건물 생성 단계로 이어집니다.
            </p>
          </div>
        </div>
        <div className="border rounded-lg p-4 space-y-3">
          <h2 className="text-lg font-semibold text-gray-800">2단계) 건물 선택/생성</h2>
          <div className="bg-gray-50 border border-dashed rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">엑셀 파일 선택</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={!selectedBuildingId || uploadingOfficetel}
              onChange={async e => {
                const file = e.target.files?.[0];
                if (!file || !selectedBuildingId) return;
                setUploadingOfficetel(true);
                try {
                  const formData = new FormData();
                  formData.append('excel', file);
                  const res = await fetch(`/api/officetel/buildings/${selectedBuildingId}/upload`, {
                    method: 'POST',
                    body: formData,
                  });
                  const body = await res.json().catch(() => ({}));
                  if (!res.ok) {
                    throw new Error(body.message || '업로드 실패');
                  }
                  toast.success(body.message || '업로드 완료');
                  await refetchUnits();
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : '업로드 실패');
                } finally {
                  setUploadingOfficetel(false);
                  e.target.value = '';
                }
              }}
              className="w-full text-sm"
            />
            <p className="text-xs text-gray-500 mt-2">
              예시) 첫 행: 라인 헤더, 첫 열: 층 번호(8,7,6...). 셀 값: 701-1, 701-2 등 호수 표현. 중복/빈 값은 무시됩니다.
            </p>
            {!selectedBuildingId && (
              <p className="text-xs text-red-600 mt-1">먼저 단지/건물을 선택하세요.</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 border rounded-lg p-4 space-y-3">
          <h2 className="text-lg font-semibold text-gray-800">건물 선택/생성</h2>
          {complexesLoading ? (
            <p className="text-sm text-gray-500">단지 목록을 불러오는 중...</p>
          ) : (
            <>
              <label className="block text-sm text-gray-700">단지</label>
              <select
                value={selectedComplexId ?? ''}
                onChange={e => setSelectedComplexId(Number(e.target.value) || null)}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                {(showAllComplexes ? complexesData?.data : officetelComplexes)?.map((complex: any) => (
                  <option key={complex.id} value={complex.id}>
                    {complex.name} ({complex.buildings?.length ?? 0}동)
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                <input
                  type="checkbox"
                  checked={showAllComplexes}
                  onChange={e => setShowAllComplexes(e.target.checked)}
                  className="rounded"
                />
                <span>모든 단지 보기 (기본은 오피스텔 단지만 표시)</span>
              </label>
              <label className="block text-sm text-gray-700 mt-2">
                건물(동) {forceSelectAllBuildings ? '(전체 표시)' : '(오피스텔만)'}
              </label>
              <select
                value={selectedBuildingId ?? ''}
                onChange={e => setSelectedBuildingId(Number(e.target.value) || null)}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                {complexesData?.data
                  ?.find((c: any) => c.id === selectedComplexId)
                  ?.buildings?.filter((b: any) =>
                    forceSelectAllBuildings ? true : b.buildingType === 'officetel'
                  )
                  ?.map((b: any) => (
                    <option key={b.id} value={b.id}>
                      {b.dongNumber}동 ({b.buildingType || '미분류'} • 세대 {b._count?.units ?? 0})
                    </option>
                  ))}
              </select>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={forceSelectAllBuildings}
                  onChange={e => setForceSelectAllBuildings(e.target.checked)}
                  className="rounded"
                />
                <span>오피스텔로 분류되지 않은 동도 표시</span>
              </div>
              {selectedBuildingId && (() => {
                const complex = complexesData?.data?.find((c: any) => c.id === selectedComplexId);
                const building = complex?.buildings?.find((b: any) => b.id === selectedBuildingId);
                if (!building) return null;
                if (building.buildingType !== 'officetel') {
                  return (
                    <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2">
                      이 동은 오피스텔로 분류되지 않았습니다.
                      <button
                        className="ml-2 text-red-700 underline"
                        onClick={() => setBuildingTypeOfficetel.mutate(selectedBuildingId)}
                        disabled={setBuildingTypeOfficetel.isPending}
                      >
                        {setBuildingTypeOfficetel.isPending ? '변경 중...' : '오피스텔로 설정'}
                      </button>
                    </div>
                  );
                }
                return (
                  <div className="text-xs text-green-700 bg-green-50 border border-green-100 rounded p-2">
                    오피스텔 건물입니다. 업로드 후 메트릭스를 확인하세요.
                  </div>
                );
              })()}
              <button
                onClick={() => refetchUnits()}
                disabled={!selectedBuildingId}
                className="w-full bg-gray-800 hover:bg-gray-900 text-white text-sm py-2 rounded mt-2 disabled:opacity-50"
              >
                세대 메트릭스 불러오기
              </button>
              <p className="text-xs text-gray-500">
                업로드 후 새로고침 없이도 불러오기 버튼으로 메트릭스를 갱신할 수 있습니다.
              </p>
            </>
          )}
        </div>
        <div className="border rounded-lg p-4 space-y-3">
          <h2 className="text-lg font-semibold text-gray-800">새 오피스텔 건물 생성</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">동 번호</label>
              <input
                type="number"
                value={newDongNumber}
                onChange={e => setNewDongNumber(parseInt(e.target.value) || newDongNumber)}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">최고 층</label>
              <input
                type="number"
                value={newMaxFloor}
                onChange={e => setNewMaxFloor(parseInt(e.target.value) || newMaxFloor)}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-700 mb-1">호수 패턴</label>
              <input
                type="text"
                value={newUnitPatternInput}
                onChange={e => setNewUnitPatternInput(e.target.value)}
                className="w-full border rounded px-2 py-1"
                placeholder="예: 1,2,3,4"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">단지</label>
              <select
                value={newComplexId ?? selectedComplexId ?? ''}
                onChange={e => setNewComplexId(e.target.value ? Number(e.target.value) : null)}
                className="w-full border rounded px-2 py-1 text-sm"
              >
                <option value="">(현재 선택 단지 또는 단독)</option>
                {officetelComplexes.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => createOfficetelBuilding.mutate()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded disabled:opacity-50"
              disabled={createOfficetelBuilding.isPending}
            >
              {createOfficetelBuilding.isPending ? '생성 중...' : '오피스텔 건물 생성'}
            </button>
            <p className="text-xs text-gray-500 self-center">
              단지를 지정하지 않으면 자동 생성된 단독 단지로 등록합니다.
            </p>
          </div>
        </div>
      </div>

      <div className="border rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">오피스텔 건물 관리</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">동 번호</label>
            <input
              type="number"
              value={newDongNumber}
              onChange={e => setNewDongNumber(parseInt(e.target.value) || newDongNumber)}
              className="w-full border rounded px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">최고 층</label>
            <input
              type="number"
              value={newMaxFloor}
              onChange={e => setNewMaxFloor(parseInt(e.target.value) || newMaxFloor)}
              className="w-full border rounded px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">호수 패턴</label>
            <input
              type="text"
              value={newUnitPatternInput}
              onChange={e => setNewUnitPatternInput(e.target.value)}
              className="w-full border rounded px-2 py-1"
              placeholder="예: 1,2,3,4"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">단지 (선택)</label>
            <select
              value={newComplexId ?? ''}
              onChange={e => setNewComplexId(e.target.value ? Number(e.target.value) : null)}
              className="w-full border rounded px-2 py-1 text-sm"
            >
              <option value="">단독(자동 생성)</option>
              {complexesData?.data?.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => createOfficetelBuilding.mutate()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded disabled:opacity-50"
            disabled={createOfficetelBuilding.isPending}
          >
            {createOfficetelBuilding.isPending ? '생성 중...' : '오피스텔 건물 생성'}
          </button>
          <p className="text-xs text-gray-500 self-center">
            단지 미선택 시 오피스텔 단독 단지가 자동 생성됩니다.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="border rounded-lg p-4 space-y-3">
          <h2 className="text-lg font-semibold text-gray-800">라인/층 설정</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">라인 목록 (쉼표 구분)</label>
            <input
              type="text"
              value={linesInput}
              onChange={e => setLinesInput(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="예: 801,802,803,804,805,806,807,808,809,810"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">최상층</label>
              <input
                type="number"
                value={floorTop}
                onChange={e => setFloorTop(parseInt(e.target.value) || floorTop)}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">최하층</label>
              <input
                type="number"
                value={floorBottom}
                onChange={e => setFloorBottom(parseInt(e.target.value) || floorBottom)}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">층 묶음(개)</label>
              <input
                type="number"
                value={floorsPerZone}
                min={1}
                onChange={e => setFloorsPerZone(Math.max(1, parseInt(e.target.value) || floorsPerZone))}
                className="w-full border rounded px-2 py-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">라인 묶음(개)</label>
              <input
                type="number"
                value={linesPerZone}
                min={1}
                onChange={e => setLinesPerZone(Math.max(1, parseInt(e.target.value) || linesPerZone))}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div className="flex items-end">
              <p className="text-xs text-gray-500">
                기본 권장: 라인 2~3개 × 층 3층 단위. 변형 라인은 오버라이드로 전수조사.
              </p>
            </div>
          </div>
        </div>

        <div className="border rounded-lg p-4 space-y-3">
          <h2 className="text-lg font-semibold text-gray-800">간격 규칙(gapRule)</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">기본 간격(visitEvery)</label>
              <input
                type="number"
                min={1}
                value={defaultVisitEvery}
                onChange={e => setDefaultVisitEvery(Math.max(1, parseInt(e.target.value) || defaultVisitEvery))}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">예외 호수(쉼표 구분)</label>
              <input
                type="text"
                value={exceptionsInput}
                onChange={e => setExceptionsInput(e.target.value)}
                className="w-full border rounded px-2 py-1"
                placeholder="-1,-2,한자리,끝호수"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">오버라이드 라인(쉼표 구분)</label>
              <input
                type="text"
                value={overrideLinesInput}
                onChange={e => setOverrideLinesInput(e.target.value)}
                className="w-full border rounded px-2 py-1"
                placeholder="804,805"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">오버라이드 간격</label>
              <input
                type="number"
                min={1}
                value={overrideVisitEvery}
                onChange={e => setOverrideVisitEvery(Math.max(1, parseInt(e.target.value) || overrideVisitEvery))}
                className="w-full border rounded px-2 py-1"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            불규칙 라인(-1/-2/상가/펜트)과 한 자리 호수 라인은 전수조사(간격 1) 권장. 간격이 커질수록 끝호수는 예외로 방문.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {zonePlans.map(zone => {
          const floorsLabel = `${zone.floorRange.top}층~${zone.floorRange.bottom}층`;
          const overrideHit = zone.lines.some(line => zone.overrides.includes(line));
          return (
            <div key={`${zone.name}-${floorsLabel}-${zone.lines.join('-')}`} className="border rounded-lg p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{zone.name}</h3>
                <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-100">
                  {floorsLabel}
                </span>
              </div>
              <p className="text-sm text-gray-700 mb-1">라인: {zone.lines.join(', ')}</p>
              <p className="text-sm text-gray-700 mb-1">
                기본 간격: {zone.visitEvery}호 {overrideHit ? '(일부 라인 오버라이드)' : ''}
              </p>
              {overrideHit && (
                <p className="text-xs text-gray-600 mb-1">
                  오버라이드 라인: {zone.overrides.join(', ')} → {overrideVisitEvery}호
                </p>
              )}
              <p className="text-xs text-gray-500">예외: {zone.exceptions.join(', ') || '없음'}</p>
              <div className="mt-3 text-xs text-gray-500">
                1차: 간격 방문 → 2차: 미방문-간격 보완. 라인×층 셀 상태(완료/미방문-간격/예외/비거주)로 체크하세요.
              </div>
            </div>
          );
        })}
      </div>

      {complexesData?.data && (
        <div className="mt-8 border rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">오피스텔 건물 목록</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {complexesData.data.flatMap((c: any) =>
              (c.buildings || [])
                .filter((b: any) => b.buildingType === 'officetel')
                .map((b: any) => (
                  <div key={b.id} className="border rounded p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold">{b.dongNumber}동</div>
                      <span className="text-xs text-gray-500">{c.name}</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      세대: {b._count?.units ?? 0} · 최고층: {b.maxFloor}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <button
                        className="text-xs text-blue-600 underline"
                        onClick={() => {
                          setSelectedComplexId(c.id);
                          setSelectedBuildingId(b.id);
                        }}
                      >
                        선택
                      </button>
                      <button
                        className="text-xs text-red-600 underline"
                        onClick={() => {
                          if (window.confirm(`${b.dongNumber}동을 삭제할까요? 세대도 함께 삭제됩니다.`)) {
                            deleteOfficetelBuilding.mutate(b.id);
                          }
                        }}
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      )}

      <div className="mt-8 border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">세대 메트릭스</h2>
          {selectedBuildingId && (
            <Link to="/cards" className="text-sm text-blue-600 hover:underline">
              카드 상세로 이동
            </Link>
          )}
        </div>
        {unitsLoading ? (
          <p className="text-sm text-gray-500">세대 정보를 불러오는 중...</p>
        ) : !matrix ? (
          <p className="text-sm text-gray-500">건물을 선택하고 세대 메트릭스를 불러오세요.</p>
        ) : (
          <div className="overflow-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">층</th>
                  {matrix.lines.map(line => (
                    <th key={line} className="px-3 py-2 text-center text-xs font-semibold text-gray-700">
                      라인 {line}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.floors.map((floor, rowIdx) => (
                  <tr key={floor} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-2 font-semibold text-gray-800">{floor}층</td>
                {(matrix.grid[rowIdx] || []).map((cell, cellIdx) => (
                  <td
                    key={`${floor}-${matrix.lines[cellIdx]}`}
                    className="px-3 py-2 text-center whitespace-nowrap"
                  >
                    {cell ? cell.code || `${cell.floor}${(cell.suffix ?? '').toString().padStart(2, '0')}` : '—'}
                  </td>
                ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          onClick={() => generateCardsMutation.mutate()}
          disabled={!selectedBuildingId || generateCardsMutation.isPending}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50"
        >
          {generateCardsMutation.isPending ? '카드 생성 중...' : '이 건물로 카드 생성'}
        </button>
        <p className="text-xs text-gray-500">
          선택한 건물의 세대 메트릭스를 기반으로 gapRule(오피스텔) 규칙을 적용해 카드를 생성합니다.
        </p>
      </div>
    </div>
  );
};

export default OfficetelZonePage;
